import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Save, User, Edit2 } from "lucide-react";
import { useAuth, apiCall } from "../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";

// City options
const CITIES = ["Delhi", "Mumbai", "Bangalore", "Chennai", "Kolkata", "Hyderabad", "Pune", "Ahmedabad"];
const RELIGIONS = ["Hindu", "Muslim", "Christian", "Sikh", "Buddhist", "Jain"];
const COUNTRY_CODES = ["+91 India", "+1 USA", "+44 UK", "+971 UAE"];

const Profile = () => {
  const { user, token, updateUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showOtherCity, setShowOtherCity] = useState(false);
  const [showOtherSelfReligion, setShowOtherSelfReligion] = useState(false);
  const [showOtherSpouseReligion, setShowOtherSpouseReligion] = useState(false);
  
  const [profile, setProfile] = useState({
    // Core Identity
    full_name: "",
    email: "",
    country_code: "+91 India",
    mobile: "",
    city: "",
    city_other: "",
    age_group: "",
    gender: "",
    
    // Relational Context
    relationship_status: "",
    marriage_type: "",
    self_religion: "",
    self_religion_other: "",
    spouse_religion: "",
    spouse_religion_other: "",
    relationship_duration: "",
    separation_duration: "",
    living_situation: "",
    
    // Children
    num_kids: "0",
    kids_details: "",
    kids_custody: "",
    
    // Financial & Legal
    income_status: "",
    legal_case_status: "",
    
    // Spiritual
    spiritual_path: "",
  });

  useEffect(() => {
    const fetchProfile = async () => {
      const result = await apiCall("GET", "/profile", null, token);
      if (result.success) {
        const data = result.data;
        setProfile(prev => ({
          ...prev,
          full_name: data.full_name || data.name || "",
          email: data.email || "",
          mobile: data.mobile || "",
          city: CITIES.includes(data.city) ? data.city : (data.city ? "Other" : ""),
          city_other: !CITIES.includes(data.city) ? data.city || "" : "",
          age_group: data.age_group || "",
          gender: data.gender || "",
          relationship_status: data.relationship_status || "",
          marriage_type: data.marriage_type || "",
          self_religion: RELIGIONS.includes(data.self_religion) ? data.self_religion : (data.self_religion ? "Other" : ""),
          self_religion_other: !RELIGIONS.includes(data.self_religion) ? data.self_religion || "" : "",
          spouse_religion: RELIGIONS.includes(data.spouse_religion) ? data.spouse_religion : (data.spouse_religion ? "Other" : ""),
          spouse_religion_other: !RELIGIONS.includes(data.spouse_religion) ? data.spouse_religion || "" : "",
          relationship_duration: data.relationship_duration || "",
          separation_duration: data.separation_duration || "",
          living_situation: data.living_situation || "",
          num_kids: data.num_kids?.toString() || "0",
          kids_details: data.kids_details || "",
          kids_custody: data.kids_custody || "",
          income_status: data.income_status || "",
          legal_case_status: data.legal_case_status || "",
          spiritual_path: data.spiritual_path || "",
        }));
        
        // Set "Other" visibility
        if (data.city && !CITIES.includes(data.city)) setShowOtherCity(true);
        if (data.self_religion && !RELIGIONS.includes(data.self_religion)) setShowOtherSelfReligion(true);
        if (data.spouse_religion && !RELIGIONS.includes(data.spouse_religion)) setShowOtherSpouseReligion(true);
      }
      setLoading(false);
    };
    fetchProfile();
  }, [token]);

  const updateField = (key, value) => {
    setProfile(prev => ({ ...prev, [key]: value }));
    
    // Handle "Other" option visibility
    if (key === "city") {
      setShowOtherCity(value === "Other");
      if (value !== "Other") setProfile(prev => ({ ...prev, city_other: "" }));
    }
    if (key === "self_religion") {
      setShowOtherSelfReligion(value === "Other");
      if (value !== "Other") setProfile(prev => ({ ...prev, self_religion_other: "" }));
    }
    if (key === "spouse_religion") {
      setShowOtherSpouseReligion(value === "Other");
      if (value !== "Other") setProfile(prev => ({ ...prev, spouse_religion_other: "" }));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    
    // Prepare data - resolve "Other" values
    const saveData = {
      ...profile,
      city: profile.city === "Other" ? profile.city_other : profile.city,
      self_religion: profile.self_religion === "Other" ? profile.self_religion_other : profile.self_religion,
      spouse_religion: profile.spouse_religion === "Other" ? profile.spouse_religion_other : profile.spouse_religion,
      num_kids: parseInt(profile.num_kids) || 0,
    };
    
    // Remove temporary fields
    delete saveData.city_other;
    delete saveData.self_religion_other;
    delete saveData.spouse_religion_other;
    delete saveData.country_code;
    
    const result = await apiCall("PUT", "/profile", saveData, token);
    setSaving(false);
    
    if (result.success) {
      updateUser({ profile_complete: true });
      toast.success("Profile updated successfully");
      setIsEditing(false);
    } else {
      toast.error(result.error);
    }
  };

  const showRelationshipFields = ["Committed", "Married", "Divorced", "Separated", "Widowed", "Complicated"].includes(profile.relationship_status);
  const showSeparationField = ["Divorced", "Separated"].includes(profile.relationship_status);
  const showKidsCustody = parseInt(profile.num_kids) > 0 && showSeparationField;

  if (loading) {
    return (
      <div className="min-h-screen bg-cosmic-void flex items-center justify-center">
        <div className="text-divine-gold animate-pulse font-heading text-2xl">Loading Profile...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cosmic-void py-12 px-6" data-testid="profile-page">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate("/dashboard")}
              className="p-2 hover:bg-cosmic-light/20 rounded-lg transition-colors text-chakra-crown/70 hover:text-divine-gold"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="font-heading text-3xl text-divine-gold">My Profile</h1>
              <p className="text-chakra-crown/60 text-sm">Client ID: {user?.client_id}</p>
            </div>
          </div>
          
          {!isEditing ? (
            <Button
              onClick={() => setIsEditing(true)}
              className="vedic-button-secondary"
              data-testid="edit-profile-btn"
            >
              <Edit2 className="w-4 h-4 mr-2" />
              Edit Profile
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button
                variant="ghost"
                onClick={() => setIsEditing(false)}
                className="text-chakra-crown"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="vedic-button"
                data-testid="save-profile-btn"
              >
                {saving ? "Saving..." : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save
                  </>
                )}
              </Button>
            </div>
          )}
        </div>

        <div className="space-y-8">
          {/* SECTION 1: CORE IDENTITY */}
          <div className="sacred-card">
            <h2 className="font-heading text-xl text-divine-gold mb-6">CORE IDENTITY</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Full Name */}
              <div className="space-y-2">
                <Label className="label-sacred">Full Name *</Label>
                {isEditing ? (
                  <Input
                    value={profile.full_name}
                    onChange={(e) => updateField("full_name", e.target.value)}
                    className="bg-neutral-obsidian/50 border border-divine-gold/20 text-chakra-crown"
                    data-testid="fullname-input"
                  />
                ) : (
                  <p className="text-chakra-crown py-2">{profile.full_name || "Not specified"}</p>
                )}
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label className="label-sacred">Email *</Label>
                <p className="text-chakra-crown py-2">{profile.email}</p>
              </div>

              {/* Mobile */}
              <div className="space-y-2">
                <Label className="label-sacred">Mobile *</Label>
                {isEditing ? (
                  <div className="flex gap-2">
                    <Select value={profile.country_code} onValueChange={(v) => updateField("country_code", v)}>
                      <SelectTrigger className="w-32 bg-neutral-obsidian/50 border border-divine-gold/20 text-chakra-crown">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-cosmic-deep border border-divine-gold/20">
                        {COUNTRY_CODES.map(c => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      value={profile.mobile}
                      onChange={(e) => updateField("mobile", e.target.value)}
                      className="flex-1 bg-neutral-obsidian/50 border border-divine-gold/20 text-chakra-crown"
                      placeholder="Mobile number"
                    />
                  </div>
                ) : (
                  <p className="text-chakra-crown py-2">{profile.mobile || "Not specified"}</p>
                )}
              </div>

              {/* City */}
              <div className="space-y-2">
                <Label className="label-sacred">City *</Label>
                {isEditing ? (
                  <div className="space-y-2">
                    <Select value={profile.city} onValueChange={(v) => updateField("city", v)}>
                      <SelectTrigger className="bg-neutral-obsidian/50 border border-divine-gold/20 text-chakra-crown" data-testid="city-select">
                        <SelectValue placeholder="Select city" />
                      </SelectTrigger>
                      <SelectContent className="bg-cosmic-deep border border-divine-gold/20">
                        {CITIES.map(c => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    {showOtherCity && (
                      <Input
                        value={profile.city_other}
                        onChange={(e) => updateField("city_other", e.target.value)}
                        placeholder="Enter your city"
                        className="bg-neutral-obsidian/50 border border-divine-gold/20 text-chakra-crown"
                        data-testid="city-other-input"
                      />
                    )}
                  </div>
                ) : (
                  <p className="text-chakra-crown py-2">{profile.city === "Other" ? profile.city_other : profile.city || "Not specified"}</p>
                )}
              </div>

              {/* Age Group */}
              <div className="space-y-2">
                <Label className="label-sacred">Age Group *</Label>
                {isEditing ? (
                  <Select value={profile.age_group} onValueChange={(v) => updateField("age_group", v)}>
                    <SelectTrigger className="bg-neutral-obsidian/50 border border-divine-gold/20 text-chakra-crown">
                      <SelectValue placeholder="Select age group" />
                    </SelectTrigger>
                    <SelectContent className="bg-cosmic-deep border border-divine-gold/20">
                      {["18-25", "26-35", "36-45", "46-55", "55+"].map(a => (
                        <SelectItem key={a} value={a}>{a}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="text-chakra-crown py-2">{profile.age_group || "Not specified"}</p>
                )}
              </div>

              {/* Gender */}
              <div className="space-y-2">
                <Label className="label-sacred">Gender *</Label>
                {isEditing ? (
                  <RadioGroup
                    value={profile.gender}
                    onValueChange={(v) => updateField("gender", v)}
                    className="flex gap-4 pt-2"
                  >
                    {["Male", "Female", "Other"].map((g) => (
                      <div key={g} className="flex items-center space-x-2">
                        <RadioGroupItem value={g} id={`gender-${g}`} className="border-divine-gold/40 text-divine-gold" />
                        <Label htmlFor={`gender-${g}`} className="text-chakra-crown">{g}</Label>
                      </div>
                    ))}
                  </RadioGroup>
                ) : (
                  <p className="text-chakra-crown py-2">{profile.gender || "Not specified"}</p>
                )}
              </div>
            </div>
          </div>

          {/* SECTION 2: RELATIONAL CONTEXT */}
          <div className="sacred-card">
            <h2 className="font-heading text-xl text-divine-gold mb-6">RELATIONAL CONTEXT</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Relationship Status */}
              <div className="space-y-2">
                <Label className="label-sacred">Relationship Status *</Label>
                {isEditing ? (
                  <Select value={profile.relationship_status} onValueChange={(v) => updateField("relationship_status", v)}>
                    <SelectTrigger className="bg-neutral-obsidian/50 border border-divine-gold/20 text-chakra-crown">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent className="bg-cosmic-deep border border-divine-gold/20">
                      {["Single", "Dating", "Committed", "Married", "Divorced", "Widowed", "Separated", "Complicated"].map(s => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="text-chakra-crown py-2">{profile.relationship_status || "Not specified"}</p>
                )}
              </div>

              {/* Marriage Type - Conditional */}
              {showRelationshipFields && (
                <div className="space-y-2">
                  <Label className="label-sacred">Marriage Type</Label>
                  {isEditing ? (
                    <Select value={profile.marriage_type} onValueChange={(v) => updateField("marriage_type", v)}>
                      <SelectTrigger className="bg-neutral-obsidian/50 border border-divine-gold/20 text-chakra-crown">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent className="bg-cosmic-deep border border-divine-gold/20">
                        {["Love Marriage", "Arranged Marriage", "Love-cum-Arranged", "Not applicable"].map(t => (
                          <SelectItem key={t} value={t}>{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="text-chakra-crown py-2">{profile.marriage_type || "Not specified"}</p>
                  )}
                </div>
              )}

              {/* Self Religion - Conditional */}
              {showRelationshipFields && (
                <div className="space-y-2">
                  <Label className="label-sacred">Your Religion</Label>
                  {isEditing ? (
                    <div className="space-y-2">
                      <Select value={profile.self_religion} onValueChange={(v) => updateField("self_religion", v)}>
                        <SelectTrigger className="bg-neutral-obsidian/50 border border-divine-gold/20 text-chakra-crown">
                          <SelectValue placeholder="Select religion" />
                        </SelectTrigger>
                        <SelectContent className="bg-cosmic-deep border border-divine-gold/20">
                          {RELIGIONS.map(r => (
                            <SelectItem key={r} value={r}>{r}</SelectItem>
                          ))}
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                      {showOtherSelfReligion && (
                        <Input
                          value={profile.self_religion_other}
                          onChange={(e) => updateField("self_religion_other", e.target.value)}
                          placeholder="Enter your religion"
                          className="bg-neutral-obsidian/50 border border-divine-gold/20 text-chakra-crown"
                        />
                      )}
                    </div>
                  ) : (
                    <p className="text-chakra-crown py-2">{profile.self_religion === "Other" ? profile.self_religion_other : profile.self_religion || "Not specified"}</p>
                  )}
                </div>
              )}

              {/* Spouse Religion - Conditional */}
              {showRelationshipFields && (
                <div className="space-y-2">
                  <Label className="label-sacred">Spouse's Religion</Label>
                  {isEditing ? (
                    <div className="space-y-2">
                      <Select value={profile.spouse_religion} onValueChange={(v) => updateField("spouse_religion", v)}>
                        <SelectTrigger className="bg-neutral-obsidian/50 border border-divine-gold/20 text-chakra-crown">
                          <SelectValue placeholder="Select religion" />
                        </SelectTrigger>
                        <SelectContent className="bg-cosmic-deep border border-divine-gold/20">
                          {RELIGIONS.map(r => (
                            <SelectItem key={r} value={r}>{r}</SelectItem>
                          ))}
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                      {showOtherSpouseReligion && (
                        <Input
                          value={profile.spouse_religion_other}
                          onChange={(e) => updateField("spouse_religion_other", e.target.value)}
                          placeholder="Enter spouse's religion"
                          className="bg-neutral-obsidian/50 border border-divine-gold/20 text-chakra-crown"
                        />
                      )}
                    </div>
                  ) : (
                    <p className="text-chakra-crown py-2">{profile.spouse_religion === "Other" ? profile.spouse_religion_other : profile.spouse_religion || "Not specified"}</p>
                  )}
                </div>
              )}

              {/* Relationship Duration */}
              <div className="space-y-2">
                <Label className="label-sacred">Relationship Duration *</Label>
                {isEditing ? (
                  <Select value={profile.relationship_duration} onValueChange={(v) => updateField("relationship_duration", v)}>
                    <SelectTrigger className="bg-neutral-obsidian/50 border border-divine-gold/20 text-chakra-crown">
                      <SelectValue placeholder="Select duration" />
                    </SelectTrigger>
                    <SelectContent className="bg-cosmic-deep border border-divine-gold/20">
                      {["<1 year", "1-3 years", "3-5 years", "5-10 years", "10-15 years", "15+ years"].map(d => (
                        <SelectItem key={d} value={d}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="text-chakra-crown py-2">{profile.relationship_duration || "Not specified"}</p>
                )}
              </div>

              {/* Separation Duration - Conditional */}
              {showSeparationField && (
                <div className="space-y-2">
                  <Label className="label-sacred">Separation Duration</Label>
                  {isEditing ? (
                    <Select value={profile.separation_duration} onValueChange={(v) => updateField("separation_duration", v)}>
                      <SelectTrigger className="bg-neutral-obsidian/50 border border-divine-gold/20 text-chakra-crown">
                        <SelectValue placeholder="Select duration" />
                      </SelectTrigger>
                      <SelectContent className="bg-cosmic-deep border border-divine-gold/20">
                        {["Not separated", "<6 months", "6-12 months", "1-2 years", "2+ years"].map(d => (
                          <SelectItem key={d} value={d}>{d}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="text-chakra-crown py-2">{profile.separation_duration || "Not specified"}</p>
                  )}
                </div>
              )}

              {/* Living Situation */}
              <div className="space-y-2">
                <Label className="label-sacred">Living Situation</Label>
                {isEditing ? (
                  <Select value={profile.living_situation} onValueChange={(v) => updateField("living_situation", v)}>
                    <SelectTrigger className="bg-neutral-obsidian/50 border border-divine-gold/20 text-chakra-crown">
                      <SelectValue placeholder="Select situation" />
                    </SelectTrigger>
                    <SelectContent className="bg-cosmic-deep border border-divine-gold/20">
                      {["Separate house (Independent)", "Shared house (Family/In-laws)", "Same house as partner"].map(s => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="text-chakra-crown py-2">{profile.living_situation || "Not specified"}</p>
                )}
              </div>
            </div>
          </div>

          {/* SECTION 3: CHILDREN */}
          <div className="sacred-card">
            <h2 className="font-heading text-xl text-divine-gold mb-6">CHILDREN</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Number of Kids */}
              <div className="space-y-2">
                <Label className="label-sacred">Number of Children</Label>
                {isEditing ? (
                  <Select value={profile.num_kids} onValueChange={(v) => updateField("num_kids", v)}>
                    <SelectTrigger className="bg-neutral-obsidian/50 border border-divine-gold/20 text-chakra-crown">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent className="bg-cosmic-deep border border-divine-gold/20">
                      {["0", "1", "2", "3", "4+"].map(n => (
                        <SelectItem key={n} value={n}>{n}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="text-chakra-crown py-2">{profile.num_kids || "0"}</p>
                )}
              </div>

              {/* Kids Details - Conditional */}
              {parseInt(profile.num_kids) > 0 && (
                <div className="space-y-2">
                  <Label className="label-sacred">Kids Details (Age/Gender)</Label>
                  {isEditing ? (
                    <Input
                      value={profile.kids_details}
                      onChange={(e) => updateField("kids_details", e.target.value)}
                      placeholder="e.g., 8/M, 5/F"
                      className="bg-neutral-obsidian/50 border border-divine-gold/20 text-chakra-crown"
                    />
                  ) : (
                    <p className="text-chakra-crown py-2">{profile.kids_details || "Not specified"}</p>
                  )}
                </div>
              )}

              {/* Kids Custody - Conditional */}
              {showKidsCustody && (
                <div className="space-y-2 md:col-span-2">
                  <Label className="label-sacred">Kids Custody</Label>
                  {isEditing ? (
                    <Select value={profile.kids_custody} onValueChange={(v) => updateField("kids_custody", v)}>
                      <SelectTrigger className="bg-neutral-obsidian/50 border border-divine-gold/20 text-chakra-crown">
                        <SelectValue placeholder="Select custody" />
                      </SelectTrigger>
                      <SelectContent className="bg-cosmic-deep border border-divine-gold/20">
                        {["Full custody with me", "Shared custody", "With spouse/ex", "With grandparents/others", "Not applicable"].map(c => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="text-chakra-crown py-2">{profile.kids_custody || "Not specified"}</p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* SECTION 4: FINANCIAL & LEGAL */}
          <div className="sacred-card">
            <h2 className="font-heading text-xl text-divine-gold mb-6">FINANCIAL & LEGAL STATUS</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Income Status */}
              <div className="space-y-2">
                <Label className="label-sacred">Income Status</Label>
                {isEditing ? (
                  <Select value={profile.income_status} onValueChange={(v) => updateField("income_status", v)}>
                    <SelectTrigger className="bg-neutral-obsidian/50 border border-divine-gold/20 text-chakra-crown">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent className="bg-cosmic-deep border border-divine-gold/20">
                      {["Independent earner", "Non-earning (dependent on spouse)", "Non-earning (independent support)", "Dual income with partner"].map(s => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="text-chakra-crown py-2">{profile.income_status || "Not specified"}</p>
                )}
              </div>

              {/* Legal Case Status */}
              <div className="space-y-2">
                <Label className="label-sacred">Legal Matrimonial Case</Label>
                {isEditing ? (
                  <Select value={profile.legal_case_status} onValueChange={(v) => updateField("legal_case_status", v)}>
                    <SelectTrigger className="bg-neutral-obsidian/50 border border-divine-gold/20 text-chakra-crown">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent className="bg-cosmic-deep border border-divine-gold/20">
                      {["No legal case", "Case initiated (Divorce/maintenance)", "Court ongoing", "Finalized (Decree received)"].map(s => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="text-chakra-crown py-2">{profile.legal_case_status || "No legal case"}</p>
                )}
              </div>
            </div>
          </div>

          {/* SECTION 5: SPIRITUAL INCLINATION */}
          <div className="sacred-card">
            <h2 className="font-heading text-xl text-divine-gold mb-6">SPIRITUAL INCLINATION</h2>
            <div className="space-y-2">
              <Label className="label-sacred">Your Natural Spiritual Path *</Label>
              {isEditing ? (
                <RadioGroup
                  value={profile.spiritual_path}
                  onValueChange={(v) => updateField("spiritual_path", v)}
                  className="grid gap-4 pt-2"
                >
                  {[
                    { value: "KARMA YOGI", desc: "Duty: I serve/structure/protect" },
                    { value: "BHAKTI YOGI", desc: "Devotion: I connect through love/music" },
                    { value: "JNANA YOGI", desc: "Wisdom: I seek truth/self-analysis" },
                    { value: "DHYAN/RAJ YOGI", desc: "Meditation: I seek inner stillness/samadhi" },
                    { value: "MATERIALIST", desc: "Practical: Simple tools only" },
                  ].map((path) => (
                    <div 
                      key={path.value}
                      className={`p-4 rounded-xl border cursor-pointer transition-all ${
                        profile.spiritual_path === path.value 
                          ? "border-divine-gold bg-divine-gold/10" 
                          : "border-divine-gold/20 hover:border-divine-gold/40"
                      }`}
                      onClick={() => updateField("spiritual_path", path.value)}
                    >
                      <div className="flex items-center gap-3">
                        <RadioGroupItem value={path.value} className="border-divine-gold/40 text-divine-gold" />
                        <div>
                          <p className="text-divine-gold font-medium">{path.value}</p>
                          <p className="text-chakra-crown/60 text-sm">{path.desc}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </RadioGroup>
              ) : (
                <div className="p-4 rounded-xl bg-cosmic-light/10 border border-divine-gold/10">
                  <p className="text-divine-gold font-medium">{profile.spiritual_path || "Not specified"}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
